/** Alerts: the client's alerts and fired history, the alert builder (opened from anywhere) and live alert toasts. */
import type { Alert, AlertEvent, AlertInput, AlertKind } from '@platform/shared';
import { api, onTokenChange } from '../lib/api';
import { disablePush as disableDevicePush, enablePush as enableDevicePush, nudgePush, pushStore, pushSupport } from '../lib/push';
import { createStore, useStore } from '../lib/store';

interface BuilderPrefill {
  kind?: AlertKind;
  symbol?: string | null;
  params?: Record<string, unknown>;
  /** edit this alert instead of creating one */
  alert?: Alert;
}

interface AlertsState {
  alerts: Alert[] | null;
  events: AlertEvent[] | null;
  builder: BuilderPrefill | null;
  toasts: AlertEvent[];
}

export const alertsStore = createStore<AlertsState>({ alerts: null, events: null, builder: null, toasts: [] });

onTokenChange((t) => {
  if (!t) alertsStore.set({ alerts: null, events: null, toasts: [] });
});

export async function loadAlerts() {
  try {
    alertsStore.set({ alerts: await api.get<Alert[]>('/api/alerts') });
  } catch {
    /* keep */
  }
}

export async function loadAlertEvents() {
  try {
    alertsStore.set({ events: await api.get<AlertEvent[]>('/api/alerts/events?limit=100') });
  } catch {
    /* keep */
  }
}

export async function createAlert(input: AlertInput): Promise<Alert> {
  const a = await api.post<Alert>('/api/alerts', input);
  alertsStore.set((s) => ({ alerts: [a, ...(s.alerts ?? [])] }));
  // A new alert is a good moment to offer push on this device.
  if (a.channels.includes('push')) nudgePush('price_alert');
  return a;
}

export async function updateAlert(id: string, patch: AlertInput | Partial<AlertInput>): Promise<Alert> {
  const a = await api.patch<Alert>(`/api/alerts/${id}`, patch);
  alertsStore.set((s) => ({ alerts: (s.alerts ?? []).map((x) => (x.id === id ? a : x)) }));
  return a;
}

export async function deleteAlert(id: string) {
  await api.del(`/api/alerts/${id}`);
  alertsStore.set((s) => ({ alerts: (s.alerts ?? []).filter((x) => x.id !== id) }));
}

export async function clearAlertHistory() {
  await api.del('/api/alerts/events');
  alertsStore.set({ events: [] });
}

export const openAlertBuilder = (prefill: BuilderPrefill = {}) => alertsStore.set({ builder: prefill });
/** Key economic events is one alert per client: open it for editing when there is one. */
export async function openKeyEvents() {
  if (!alertsStore.get().alerts) await loadAlerts();
  const a = alertsStore.get().alerts?.find((x) => x.kind === 'key_events');
  openAlertBuilder(a ? { alert: a } : { kind: 'key_events' });
}
export const closeAlertBuilder = () => alertsStore.set({ builder: null });

/** A fired alert arrived over the WS channel. */
export function alertFired(ev: AlertEvent) {
  alertsStore.set((s) => ({
    toasts: [ev, ...s.toasts.filter((t) => t.id !== ev.id)].slice(0, 3),
    events: s.events ? [ev, ...s.events.filter((t) => t.id !== ev.id)] : s.events,
    // A "once" alert switches itself off when it fires.
    alerts: s.alerts?.map((a) => (a.id === ev.alertId ? { ...a, fireCount: a.fireCount + 1, lastFiredAt: ev.firedAt, active: a.repeat === 'once' ? false : a.active } : a)) ?? s.alerts,
  }));
  window.setTimeout(() => dismissToast(ev.id), 8000);
}

export const dismissToast = (id: string) => alertsStore.set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) }));

export const useAlerts = () => useStore(alertsStore, (s) => s.alerts);
export const useAlertEvents = () => useStore(alertsStore, (s) => s.events);
export const useAlertBuilder = () => useStore(alertsStore, (s) => s.builder);
export const useAlertToasts = () => useStore(alertsStore, (s) => s.toasts);

/** Active alerts on a symbol (for the bell state on rows). */
export function activeAlertSymbols(alerts: Alert[] | null): Set<string> {
  return new Set((alerts ?? []).filter((a) => a.active && a.symbol).map((a) => a.symbol!));
}

// ---- Web Push on this device ----


// Push on this device: one implementation for alerts and notifications (lib/push.ts).
export const pushSupported = () => pushSupport() === 'supported';

/** Whether this browser is subscribed right now. */
export async function pushSubscribed(): Promise<boolean> {
  if (!pushSupported()) return false;
  const reg = await navigator.serviceWorker.getRegistration('/sw.js');
  return !!(await reg?.pushManager.getSubscription());
}

/** Ask permission, subscribe this browser and register it. Returns an error message, or null on success. */
export async function enablePush(): Promise<string | null> {
  if (!pushSupported()) return 'This browser does not support push notifications. On iPhone, add the app to your Home Screen first.';
  const perm = await enableDevicePush();
  if (perm !== 'granted') return 'Notifications are blocked for this site. Allow them in your browser settings and try again.';
  if (pushStore.get().serverEnabled === false) return 'Push notifications are not set up on this platform yet.';
  return pushStore.get().subscribed ? null : 'Could not turn on push on this device. Try again.';
}

export const disablePush = (): Promise<void> => disableDevicePush();
